import { SubmitHandler, useForm } from "react-hook-form";
import { useCallback, useRef } from "react";

import { blockIsFocused } from "blocks/types";
import { useTheme } from "lib/theme";
import { trpc } from "utils/trpc";
import { usePageContext } from "lib/context/page-context";
import BlockWrapper from "blocks/components/BlockWrapper";

import { strings } from "../strings";
import type { ZapbotBlock as ZapbotBlockType } from "../types";
import type { BlockProps } from "../../__shared__/types";
import { ZeroState } from "../components/ZeroState";
import { useTrackingContext } from "observability";
import { Zapbot } from "./Zapbot";

export const ZapbotBlock = (props: BlockProps<ZapbotBlockType>) => {
  const {
    pageId,
    projectId,
    accountId,
    focusState,
    isEditing,
    isThumbnail,
    isEmbedded,
  } = usePageContext();
  const { emitConsumerInteractionEvent } = useTrackingContext();

  const actionIsFocused: boolean =
    blockIsFocused(focusState) &&
    focusState.blockId === props.blockId &&
    focusState.data?.panel === "automation";

  const { config } = props.block;
  const theme = useTheme();

  const { data: zapbotInputsResponse } = trpc.zapbots.inputs.useQuery(
    { id: config.zapbotId as string },
    { enabled: !!accountId && !!config.zapbotId, refetchOnWindowFocus: false }
  );

  const inputs = zapbotInputsResponse?.inputs ?? [];
  const values = inputs.reduce(
    (acc, i) => ({ ...acc, [i.id]: "" }),
    {} as Record<string, string>
  );

  const run = trpc.zapbots.run.useMutation();

  const methods = useForm<typeof values>({
    mode: "onBlur",
    defaultValues: values,
  });

  const formRef = useRef<HTMLFormElement>(null);
  const handleEnter = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      formRef.current?.querySelector("button")?.click();
    }
  };

  const onSubmit = useCallback<SubmitHandler<typeof values>>(
    (data, event) => {
      event?.preventDefault();
      run.mutate({
        id: config.zapbotId as string,
        body: {
          config: Object.entries(data).reduce((patchedData, [key, value]) => {
            const input = zapbotInputsResponse?.inputs.find(
              (i) => i.id === key
            );
            if (input?.autoGeneratedId) {
              return { ...patchedData, [input.label]: value };
            }
            return { ...patchedData, [key]: value };
          }, {}),
        },
      });
      emitConsumerInteractionEvent({
        interaction_goal: "Submit input in AI prompt",
        event_action: "click",
        interfaces_project_id: projectId,
        interfaces_page_id: pageId,
        interfaces_component_type: "ai_prompt",
        embedded_flag: isEmbedded,
      });
    },
    [
      config.zapbotId,
      emitConsumerInteractionEvent,
      isEmbedded,
      pageId,
      projectId,
      run,
      zapbotInputsResponse?.inputs,
    ]
  );

  const renderBody = () => {
    if (!config.zapbotId) {
      return (
        <ZeroState
          title={strings.ZAPBOT_PROMPT_BLOCK_ZERO_STATE_TITLE}
          subtitle={strings.ZAPBOT_PROMPT_BLOCK_ZERO_STATE_DESCRIPTION}
        />
      );
    }

    return (
      <Zapbot
        {...config}
        {...methods}
        actionIsFocused={actionIsFocused}
        alignment={config.style?.alignment}
        width={config.style?.width}
        inputs={inputs}
        isEditing={isEditing}
        isLoading={run.isLoading}
        isThumbnail={isThumbnail}
        output={run.data?.output}
        onEnter={handleEnter}
        onSubmit={onSubmit}
        ref={formRef}
      />
    );
  };

  return (
    <BlockWrapper
      blockId={
        !config.zapbotId
          ? {
              blockId: props.blockId,
              data: { panel: "data" },
            }
          : props.blockId
      }
      block={props.block}
      maxWidth={
        config.style?.width
          ? theme.app.width[config.style?.width]
          : theme.app.width.full
      }
      alignment={config.style?.alignment}
    >
      {renderBody()}
    </BlockWrapper>
  );
};
